import React, { useState, useEffect, useRef } from "react";
import { getVisitorId, capturePartial } from "../hooks/useVisitor";
import { trackLead } from "../lib/metaPixel";

// The listing enquiry form. Honeypot + consent gate mirror the server. On
// success it swaps to a thank-you state.
//
// So a half-filled form is never lost: the draft is saved to localStorage (and
// restored on return), and on blur / when the visitor leaves the page we send a
// soft "partial" capture (once there's a phone/email) — creating an incomplete
// lead the agent can chase.
export function EnquiryForm({ token, onSubmit }) {
  const draftKey = `pm_enquiry_draft_${token}`;
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (saved) return { name: "", phone: "", email: "", consent: false, company: "", ...saved };
    } catch { /* ignore */ }
    return { name: "", phone: "", email: "", consent: false, company: "" };
  });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const formRef = useRef(form);
  formRef.current = form;
  const capturedRef = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // Persist the draft (minus the honeypot) so a return visit resumes it.
  useEffect(() => {
    if (state === "done") return;
    try {
      const { company, ...keep } = form;
      localStorage.setItem(draftKey, JSON.stringify(keep));
    } catch { /* ignore */ }
  }, [form, state, draftKey]);

  // Soft-capture the half-filled form. Guarded so we don't spam on every event —
  // the server also de-dupes per visitor.
  const sendPartial = () => {
    if (state === "done" || form.company) return;
    capturePartial(token, { name: form.name, phone: form.phone, email: form.email });
  };

  // Fire a partial capture when the visitor leaves the page mid-form.
  useEffect(() => {
    const onLeave = () => {
      if (state === "done") return;
      const f = formRef.current;
      if (f.company) return;
      if (String(f.phone).trim() || String(f.email).trim()) {
        capturePartial(token, { name: f.name, phone: f.phone, email: f.email });
      }
    };
    window.addEventListener("pagehide", onLeave);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onLeave();
    });
    return () => window.removeEventListener("pagehide", onLeave);
  }, [token, state]);

  async function handleSubmit(e) {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await onSubmit({
        name: form.name,
        phone: form.phone,
        email: form.email,
        consent: form.consent,
        company: form.company,
        visitor_id: getVisitorId(),
      });
      if (res.ok) {
        setState("done");
        trackLead({ content_name: "Listing enquiry" });
        try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      } else {
        setState("error");
        setError((res.errors || ["Something went wrong."]).join(" "));
      }
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.errors?.join(" ") || "Network error. Please try again.");
    }
  }

  if (state === "done") {
    return (
      <div className="rounded-xl border border-[#3F6B4C]/30 bg-[#3F6B4C]/10 p-4 font-sans text-[0.9rem] text-[#3F6B4C]">
        Thanks! Our team will reach out shortly.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="font-sans">
      {/* Honeypot */}
      <div className="absolute left-[-9999px] h-0 overflow-hidden" aria-hidden="true">
        <label>
          Company
          <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
        </label>
      </div>

      {state === "error" && (
        <div className="mb-3 rounded-lg border border-[#9E4B34]/30 bg-[#9E4B34]/10 p-3 text-[0.88rem] text-[#9E4B34]">{error}</div>
      )}

      <Field label="Name" required>
        <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
      </Field>
      <Field label="Phone" required>
        <input
          required
          type="tel"
          inputMode="numeric"
          pattern="[6-9][0-9]{9}"
          maxLength={10}
          value={form.phone}
          onChange={set("phone")}
          onBlur={sendPartial}
          placeholder="10-digit mobile number"
          className={inputCls}
        />
      </Field>
      <Field label="Email">
        <input type="email" value={form.email} onChange={set("email")} onBlur={sendPartial} placeholder="you@example.com (optional)" className={inputCls} />
      </Field>

      <label className="mb-4 mt-1 flex items-start gap-2.5 text-[0.82rem] text-[#4A473E]">
        <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-0.5" />
        <span>I agree to be contacted about this property via call, WhatsApp, SMS or email.</span>
      </label>

      <button
        type="submit"
        disabled={state === "sending"}
        className="w-full rounded-[10px] bg-[#2C4A87] px-4 py-3 text-[1rem] font-semibold text-white hover:bg-[#1F3766] disabled:opacity-60"
      >
        {state === "sending" ? "Sending…" : "Get quote"}
      </button>
    </form>
  );
}

const inputCls =
  "w-full rounded-[10px] border border-[#C9C3B4] bg-white px-[0.85rem] py-[0.7rem] text-[1rem] text-[#14130F] outline-none focus:border-[#2C4A87] focus:ring-2 focus:ring-[#2C4A87]/30";

function Field({ label, required, children }) {
  return (
    <div className="mb-[0.9rem]">
      <label className="mb-[0.35rem] block text-[0.8rem] font-semibold text-[#4A473E]">
        {label} {required && <span className="text-[#9E4B34]">*</span>}
      </label>
      {children}
    </div>
  );
}
